import {
  CurecallButton,
  CurecallConfirmationModal,
  CurecallInput,
  CurecallPhoneInput,
  CurecallSelect,
  CurecallToggle,
  CurecallTooltip,
  ECurecallButtonAppearance,
  CurecallSelectItem,
} from '@honestica/curecall-ui'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ChangeEvent, useState } from 'react'
import { TUser, TUserProject, EUserTitle, USER_LOCALES, USER_TITLES } from 'src/types/user'
import { useTranslation } from 'react-i18next'
import EmailField from 'src/components/email-field/email-field.component'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPermission } from 'src/pages/configuration/permissions/_components/permissions.type'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import { MedicalStaffMemberService } from 'src/services/medical-staff-member.service'
import { generateRandomPassword, getFormSubmitNotificationError } from './users-form.helper'
import { UserProjectService } from 'src/services/user-project.service'
import { useNotification } from 'src/providers/notification.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { UserService } from 'src/services/user.service'
import { formatPhoneToInternational } from 'src/utils/phone.util'
import { TPostModalProps, TPutModalProps, TModalProps, TUserProjectForm } from './users-table.component'
import { TNavigationLocales } from 'src/types/locales'

type TUserProjectFormValues = {
  title: TUser['title']
  firstname: TUser['firstname']
  lastname: TUser['lastname']
  email: TUser['email']
  phone: TUser['phone']
  PermissionId: number
  locale: TNavigationLocales
}

interface UserProjectFormProps {
  modalProps: undefined | TPostModalProps | TPutModalProps
  setModalProps: (modalProps: TModalProps) => void
  projectPermissionList: TPermission[]
  medicalStaffMember?: TMedicalStaffMember
}
export default function UserProjectForm({
  modalProps,
  setModalProps,
  projectPermissionList,
  medicalStaffMember,
}: UserProjectFormProps) {
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { t } = useTranslation()

  const [createMedicalStaffMember, setCreateMedicalStaffMember] = useState<boolean>(false)
  const [stafMemberAlreadyPresentWarning, setStafMemberAlreadyPresentWarning] = useState<boolean>(false)
  const [values, setValues] = useState<TUserProjectFormValues>()

  const methods = useForm<TUserProjectFormValues>({
    defaultValues: {
      title: medicalStaffMember ? 'Dr.' : (modalProps?.UserProject?.User?.title as any),
      firstname: modalProps?.UserProject?.User?.firstname || medicalStaffMember?.firstname,
      lastname: modalProps?.UserProject?.User?.lastname || medicalStaffMember?.lastname,
      email: modalProps?.UserProject?.User?.email || medicalStaffMember?.email,
      phone: formatPhoneToInternational(modalProps?.UserProject?.User?.phone || medicalStaffMember?.phone),
      PermissionId: modalProps?.UserProject?.PermissionId,
      locale: modalProps?.UserProject?.User?.locale as any,
    },
  })
  const {
    formState: { errors, defaultValues },
    handleSubmit,
    register,
    setValue,
    clearErrors,
    watch,
  } = methods

  const { data: medicalSttafMembers } = useQuery({
    queryKey: ['medical-staff-member-list'],
    queryFn: () => MedicalStaffMemberService.getMedicalStaffMemberList(project.id),
  })
  const { data: isEditableUser } = useQuery({
    queryKey: ['is-editable-user'],
    queryFn: () => UserService.getIsEditableUser(project.id, modalProps?.UserProject?.UserId as number),
    enabled: !!modalProps?.UserProject?.UserId,
  })

  const createUserProject = async (data: TUserProjectForm) => {
    try {
      const randomPassword = generateRandomPassword()
      if (!randomPassword) {
        throw new Error('Feauture not compatible with current app version. Please Update Curecall app.')
      }
      const createdUserProject = await UserProjectService.inviteUserProject(project.id, {
        PermissionId: data.PermissionId,
        isActive: false,
        User: {
          ...data.User,
          password: randomPassword,
        },
      })
      if (data.medicalStaffMember) {
        await MedicalStaffMemberService.createMedicalStaffMember(project.id, {
          ...data.medicalStaffMember,
          UserId: createdUserProject.UserId,
        })
      }
      queryClient.setQueryData(['user-project-list'], (olData: Array<TUserProject> | undefined) => {
        return olData ? [...olData, createdUserProject] : [createdUserProject]
      })
      setModalProps({
        mode: 'SUMMARY',
        UserProject: createdUserProject,
        password: randomPassword,
      })
    } catch (error: unknown) {
      notify(getFormSubmitNotificationError(getErrorMessage(error), t), 10000)
    }
  }

  const updateUserProject = async (data: { form: TUserProjectForm; isEditableUser?: boolean }) => {
    try {
      const updatedUserProject = await UserProjectService.updateUserProject(
        project.id,
        data.form,
        data.isEditableUser,
      )
      setModalProps(undefined)

      queryClient.setQueryData(['user-project-list'], (oldData: Array<TUserProject> | undefined) =>
        oldData?.map((oldUserProject) =>
          oldUserProject.UserId === updatedUserProject.UserId ? updatedUserProject : oldUserProject,
        ),
      )

      const firstname = updatedUserProject?.User?.firstname
      const lastname = updatedUserProject?.User?.lastname
      const userTtitle = updatedUserProject?.User?.title
      notify({
        appearance: 'SUCCESS',
        message: updatedUserProject.isActive
          ? t('pages.users-page.notify-user-activated-description', {
              firstname,
              lastname,
              civility: userTtitle,
            })
          : t('pages.users-page.notify-user-disabled-description', {
              firstname,
              lastname,
              civility: userTtitle,
            }),
        title: updatedUserProject.isActive
          ? t('pages.users-page.notify-user-activated-title')
          : t('pages.users-page.notify-user-disabled-title'),
      })
    } catch (error) {
      notify(getFormSubmitNotificationError(getErrorMessage(error), t), 10000)
    }
  }

  const onSubmit: SubmitHandler<TUserProjectFormValues> = async (values: TUserProjectFormValues) => {
    if (!values || !modalProps) return

    let medicalStaffMember = undefined

    if (createMedicalStaffMember) {
      const existStaffMember = medicalSttafMembers?.find(
        (item) =>
          item?.firstname?.trim().toLowerCase() === values.firstname?.trim().toLowerCase() &&
          item?.lastname?.trim().toLowerCase() === values.lastname?.trim().toLowerCase(),
      )
      if (existStaffMember && !stafMemberAlreadyPresentWarning) {
        setStafMemberAlreadyPresentWarning(true)
        setValues(values)
        return
      }
      medicalStaffMember = {
        civility: EUserTitle.DR,
        firstname: values.firstname,
        lastname: values.lastname,
        phone: values?.phone?.trim() || null,
        email: values.email,
        professionalCode: '10',
        professionalLabel: 'Médecin',
      } as TMedicalStaffMember
    }
    const User = {
      title: values.title,
      firstname: values.firstname,
      lastname: values.lastname,
      phone: values.phone ? formatPhoneToInternational(values.phone) : null,
      email: values.email,
      locale: values.locale,
    }

    if (modalProps.mode === 'POST') {
      await createUserProject({ PermissionId: values.PermissionId, User, medicalStaffMember })
    }
    if (modalProps.mode === 'PUT') {
      await updateUserProject({
        form: {
          UserId: modalProps.UserProject.UserId,
          PermissionId: values.PermissionId,
          User,
        },
        isEditableUser,
      })
    }
    setStafMemberAlreadyPresentWarning(false)
  }

  const getSelectedPermission = (
    projectPermissionList: Array<Pick<TPermission, 'id' | 'name' | 'grants'>>,
  ) => {
    return projectPermissionList?.reduce(
      (acc, cur) => {
        const selectedItem =
          watch('PermissionId') === cur.id
            ? {
                label: cur.name,
                value: cur.id,
              }
            : undefined
        return selectedItem ?? acc
      },
      {} as { label: string; value: number } | undefined,
    )
  }

  const userLocaleOptions =
    USER_LOCALES?.map((item) => ({
      label: t(`pages.users-page.${item.locale}`),
      value: item.code,
    })) ?? []

  return (
    <FormProvider {...methods}>
      <form
        action="#"
        method="POST"
        onSubmit={handleSubmit(onSubmit)}
        className="c-flex c-flex-col c-gap-4 c-pb-8"
      >
        <div className="c-flex c-flex-col c-gap-2">
          <div className="c-flex c-w-full [&>*]:c-w-[100%] c-gap-8 c-mb-5">
            <CurecallSelect
              {...register('locale', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              buttonClassName="tw-text-base"
              label={t('pages.users-page.form-user-locale-input')}
              placeholder={t('pages.users-page.form-user-locale-input')}
              items={userLocaleOptions}
              selectedItem={userLocaleOptions.find((item) => item.value === watch('locale'))}
              errorMessage={errors.PermissionId && t('pages.users-page.form-input-error')}
              onChange={(selected: CurecallSelectItem) => {
                clearErrors('locale')
                setValue('locale', selected.value)
              }}
            />
            <CurecallSelect
              {...register('title', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              isDisabled={modalProps?.mode === 'PUT' && !isEditableUser}
              buttonClassName="tw-text-base"
              label={t('pages.users-page.form-user-title-input')}
              placeholder={t('pages.users-page.form-user-title-input')}
              items={USER_TITLES.map((title) => ({ label: title, value: title }))}
              selectedItem={{
                label: watch('title') as (typeof USER_TITLES)[number],
                value: watch('title'),
              }}
              errorMessage={errors?.title && t('pages.users-page.form-input-error')}
              onChange={(item: { label: any; value: (typeof USER_TITLES)[number] }) => {
                clearErrors('title')
                setValue('title', item.value)
              }}
            />
            <CurecallSelect
              {...register('PermissionId', {
                required: t('pages.users-page.form-input-error'),
                valueAsNumber: true,
              })}
              isRequired={true}
              buttonClassName="tw-text-base"
              label={t('pages.users-page.form-permission-input')}
              placeholder={t('pages.users-page.form-permission-input')}
              items={
                projectPermissionList?.map((projectPermission) => ({
                  label: projectPermission.name,
                  value: projectPermission.id,
                })) ?? []
              }
              selectedItem={projectPermissionList && getSelectedPermission(projectPermissionList)}
              errorMessage={errors.PermissionId && t('pages.users-page.form-input-error')}
              onChange={(selected: { label: any; value: number }) => {
                clearErrors('PermissionId')
                setValue('PermissionId', selected.value)
              }}
            />
          </div>
          <div className="c-flex c-w-full [&>*]:c-w-[100%] c-gap-8">
            <CurecallInput
              {...register('firstname', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              isDisabled={modalProps?.mode === 'PUT' && !isEditableUser}
              label={t('pages.users-page.form-firstname-input')}
              placeholder={t('pages.users-page.form-firstname-input')}
              errorMessage={errors?.firstname && t('pages.users-page.form-input-error')}
              onChange={(item: ChangeEvent<HTMLInputElement>) => {
                clearErrors('firstname')
                setValue('firstname', item.target.value)
              }}
            />
            <CurecallInput
              {...register('lastname', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              isDisabled={modalProps?.mode === 'PUT' && !isEditableUser}
              label={t('pages.users-page.form-lastname-input')}
              placeholder={t('pages.users-page.form-lastname-input')}
              errorMessage={errors?.lastname && t('pages.users-page.form-input-error')}
              onChange={(item: ChangeEvent<HTMLInputElement>) => {
                clearErrors('lastname')
                setValue('lastname', item.target.value)
              }}
            />
            <EmailField
              name="email"
              isDisabled={modalProps?.mode === 'PUT'}
              requiredMessageError={t('pages.users-page.form-input-error')}
            />
          </div>
          <div className="c-flex c-w-full [&>*]:c-w-[100%] c-gap-8 c-mt-4">
            <CurecallPhoneInput
              {...register('phone')}
              isDisabled={modalProps?.mode === 'PUT' && !isEditableUser}
              label={t('pages.users-page.form-phone-input')}
              placeholder={t('pages.users-page.form-phone-input')}
              notValidPhoneErrorMessage={t('pages.users-page.form-phone-invalid-error')}
              notCompletePhoneNumberErrorMessage={t('pages.users-page.form-phone-incomplete-error')}
              defaultValue={defaultValues?.phone as any}
              errorMessage={errors?.phone && t('pages.users-page.form-input-error')}
              tooltipContent={t('pages.users-page.tooltip-phone')}
              onChange={(phone) => {
                clearErrors('phone')
                setValue('phone', phone)
              }}
            />
            <div></div>
            <div></div>
          </div>
          {!medicalStaffMember && modalProps?.mode === 'POST' && (
            <div className="c-flex c-items-start c-mt-3">
              <CurecallTooltip
                content={t('pages.users-page.tooltip-create-medical-staff-member')}
                tooltipPosition="right"
              >
                <CurecallToggle
                  leftLabel={t('pages.users-page.create-medical-staff-member-an-account')}
                  isEnabled={createMedicalStaffMember}
                  onChange={setCreateMedicalStaffMember}
                />
              </CurecallTooltip>
            </div>
          )}

          <div className="c-w-[30%]"></div>
        </div>
        <div className="c-flex c-justify-center c-gap-4 c-mt-8">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            onClick={() => setModalProps(undefined)}
          >
            {t('pages.users-page.cta-cancel')}
          </CurecallButton>
          <CurecallButton appearance={ECurecallButtonAppearance.SECONDARY} type="submit">
            {modalProps?.mode === 'PUT' ? t('pages.users-page.cta-update') : t('pages.users-page.cta-add')}
          </CurecallButton>
        </div>
      </form>

      <CurecallConfirmationModal
        color="ALERT"
        title={t('pages.users-page.create-medical-staff-member-an-account-warning-title', {
          doctorName: `${values?.firstname} ${values?.lastname}`,
        })}
        isOpen={stafMemberAlreadyPresentWarning}
        cancelButtonLabel={t('pages.users-page.create-medical-staff-member-an-account-warning-cancel')}
        confirmButtonLabel={t('pages.users-page.create-medical-staff-member-an-account-warning-confirm')}
        onCancel={() => setStafMemberAlreadyPresentWarning(false)}
        onConfirm={() => values && onSubmit(values)}
      />
    </FormProvider>
  )
}
